import { generateClient } from '@aws-amplify/api';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  ItemConsumptionByEventId,
  cashlessTransactionByGuestID,
} from '../graphql/queries';
import { HeadCell, ListingVariables } from '../models/app';
import { setListing, setNextToken } from '../store/ducks/itemConsumption';
import { ItemConsumption } from './../models/GQL_API';
import useApp from './useApp';
import { toDayRangeIso } from '../helpers/dateRange';

const client = generateClient();
const useItemConsumption = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { showError } = useApp();
  const itemConsumptionListing = useSelector(
    (state: any) => state.itemConsumption.listing,
  );
  const newFirstFetch = useSelector((state: any) => state.app.firstFetch);

  const selectedAdmin = useSelector((state: any) => state.admins.selected);

  const nextToken = useSelector(
    (state: any) => state[`itemConsumption`]['nextToken'],
  );

  const reduxListing = useSelector(
    (state: any) => state[`itemConsumption`]['listing'],
  );
  const session = useSelector((state: any) => state.app.session);

  /**
   * ItemConsumptionByEventId variables for the page's filters (event, admin,
   * search, whole local days [from 00:00, to 23:59:59.999]). Null when the
   * report has nothing to query yet (no event or admin).
   */
  const buildQueryVariables = (
    searchText: string,
    generalFilters: ListingVariables['generalFilters'],
    admin: any,
  ): any | null => {
    if (!generalFilters?.event || !admin) return null;

    const filter: any = {
      deleted: { eq: '0' },
      createdByID: { eq: admin.id },
    };
    filter.or = [];
    if (searchText.length > 0) {
      if (/[a-zA-Z]/.test(searchText)) {
        filter.or.push({
          itemName: { contains: searchText.toLocaleLowerCase() },
        });
      } else if (/[0-9]/.test(searchText)) {
        // itemPrice is a Float: `contains` is invalid there, match exactly.
        const price = Number(searchText.trim());
        if (searchText.trim() !== '' && Number.isFinite(price))
          filter.or.push({ itemPrice: { eq: price } });
      }
    }
    if (filter.or.length === 0) {
      delete filter.or;
    }

    const variables: any = {
      filter,
      sortDirection: 'DESC',
      eventId: generalFilters.event,
    };
    // The old range ended at the NEXT day's midnight (UTC-parsed), so it
    // included items from the day after the selected range.
    const createdAtRange = toDayRangeIso(
      generalFilters.fromDate,
      generalFilters.toDate,
    );
    if (createdAtRange) {
      variables.createdAt = { between: createdAtRange };
    }
    return variables;
  };

  async function fetch(params: ListingVariables) {
    try {
      const { searchText, generalFilters } = params;

      const baseVariables = buildQueryVariables(
        searchText,
        generalFilters,
        selectedAdmin,
      );
      if (!baseVariables) return itemConsumptionListing;

      const variables: any = {
        ...baseVariables,
        limit: 100000,
        nextToken: nextToken,
      };

      const groupsList: any = await client.graphql<ItemConsumption>({
        query: ItemConsumptionByEventId,
        variables: variables,
        authMode: 'userPool',
      });

      const currentNextToken =
        groupsList.data.ItemConsumptionByEventId.nextToken;
      const responseListing = groupsList.data.ItemConsumptionByEventId.items;
      // Filters changed while this page was loading: drop the old response.
      if (params.isStale?.()) return [];

      let listing = [...reduxListing, ...responseListing];
      // if (responseListing?.length > 0) {
      dispatch(setListing(listing));
      // }
      dispatch(setNextToken(currentNextToken));
      return listing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  /**
   * Export: every item matching the page's current filters (all pages of
   * the index query), not only the rows loaded in the table. Filters are
   * read from the store at call time; params can override them.
   */
  async function exportAll(params: Partial<ListingVariables> = {}) {
    const state: any = store.getState();
    const pageFilters = state?.generalFilters ?? {};
    const generalFilters = { ...pageFilters, ...(params.generalFilters ?? {}) };
    const searchText = String(
      params.searchText ?? pageFilters.searchText ?? '',
    );
    const baseVariables = buildQueryVariables(
      searchText,
      generalFilters,
      state?.admins?.selected,
    );
    if (!baseVariables) return [];

    let allItems: any[] = [];
    let currentNextToken: string | null | undefined = undefined;
    do {
      const result: any = await client.graphql<ItemConsumption>({
        query: ItemConsumptionByEventId,
        variables: {
          ...baseVariables,
          // Same page size as the list (DynamoDB still caps a page at 1MB).
          limit: 100000,
          nextToken: currentNextToken,
        },
        authMode: 'userPool',
      });
      const page = result.data.ItemConsumptionByEventId;
      allItems = allItems.concat(page?.items ?? []);
      currentNextToken = page?.nextToken;
    } while (currentNextToken);
    return allItems;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'itemImage',
      numeric: false,
      disablePadding: false,
      label: 'Item Image',
    },
    {
      id: 'itemName',
      numeric: false,
      disablePadding: false,
      label: 'Item Name',
    },
    {
      id: 'itemQuantity',
      numeric: false,
      disablePadding: false,
      label: 'Sold Quantity',
    },
    {
      id: 'itemPrice',
      numeric: false,
      disablePadding: false,
      label: 'Item Price',
    },
    {
      id: 'totalPayment',
      numeric: false,
      disablePadding: false,
      label: 'Total Payment',
    },
    {
      id: 'event',
      numeric: false,
      disablePadding: false,
      label: 'Event',
    },
  ];
  const dataCells: readonly string[] = [
    'itemImage',
    'itemName',
    'itemQuantity',
    'itemPrice',
    'totalPayment',
    'event',
  ];
  const api: any = {};

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}ChangeListing`] = (listing: ItemConsumption[]) =>
    dispatch(setListing(listing));
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = reduxListing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  return api;
};
export default useItemConsumption;
